import { useState } from "react";
import { Mail, Clock, Send, Phone } from "lucide-react";
import { toast } from "sonner";
import { useLanguage } from "@/context/LanguageContext";
import { Reveal, Eyebrow } from "@/components/Reveal";
import { submitContact } from "@/lib/api";

export default function ContactSection() {
  const { t } = useLanguage();
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "", website: "" });
  const [sending, setSending] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await submitContact(form);
      toast.success(t.contact.success);
      setForm({ name: "", email: "", subject: "", message: "", website: "" });
    } catch (err) {
      toast.error(err?.response?.status === 429 ? t.common.rateLimited : t.contact.error);
    } finally {
      setSending(false);
    }
  };

  const inputCls =
    "w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-lime-500 focus:border-lime-500 transition-[box-shadow,border-color]";

  return (
    <section id="contact" data-testid="contact-section" className="relative pt-32 sm:pt-40 pb-20 sm:pb-28 bg-white grain">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 grid grid-cols-1 lg:grid-cols-12 gap-12">
        <div className="lg:col-span-5">
          <Reveal>
            <Eyebrow>{t.contact.eyebrow}</Eyebrow>
          </Reveal>
          <Reveal delay={0.1}>
            <h2 className="mt-4 font-display font-bold tracking-tight leading-snug text-2xl sm:text-3xl text-slate-900 max-w-md" data-testid="contact-title">
              {t.contact.subtitle}
            </h2>
          </Reveal>
          <Reveal delay={0.3}>
            <div className="mt-10 space-y-5">
              <div className="flex items-center gap-4">
                <span className="w-11 h-11 rounded-2xl bg-lime-50 border border-lime-200 flex items-center justify-center shrink-0">
                  <Mail size={18} className="text-lime-700" />
                </span>
                <div>
                  <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider">{t.contact.emailLabel}</p>
                  <a href="mailto:info@powerof-freedom.com" data-testid="contact-email-link" className="text-base font-bold text-slate-900 hover:text-lime-600 transition-colors" dir="ltr">
                    {t.contact.emailValue}
                  </a>
                </div>
              </div>
              <div className="flex items-center gap-4">
                <span className="w-11 h-11 rounded-2xl bg-lime-50 border border-lime-200 flex items-center justify-center shrink-0">
                  <Phone size={18} className="text-lime-700" />
                </span>
                <div>
                  <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider">{t.contact.phoneLabel}</p>
                  <a href="tel:+962791337036" data-testid="contact-phone-link" className="text-base font-bold text-slate-900 hover:text-lime-600 transition-colors" dir="ltr">
                    +962 79 133 7036
                  </a>
                </div>
              </div>
              <div className="flex items-center gap-4">
                <span className="w-11 h-11 rounded-2xl bg-teal-50 border border-teal-100 flex items-center justify-center shrink-0">
                  <Clock size={18} className="text-teal-700" />
                </span>
                <p className="text-sm text-slate-500">{t.contact.note}</p>
              </div>
            </div>
          </Reveal>
        </div>

        <div className="lg:col-span-7">
          <Reveal delay={0.2}>
            <form data-testid="contact-form" onSubmit={onSubmit} className="rounded-[2rem] bg-[#FAFAFA] border border-slate-200/80 p-6 sm:p-10 shadow-sm space-y-5">
              <input type="text" name="website" value={form.website} onChange={set("website")} tabIndex={-1} autoComplete="off" aria-hidden="true" className="hidden" data-testid="contact-honeypot" />
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <div>
                  <label htmlFor="contact-name" className="block text-sm font-semibold text-slate-700 mb-2">{t.contact.name}</label>
                  <input id="contact-name" data-testid="contact-name-input" required minLength={2} value={form.name} onChange={set("name")} placeholder={t.contact.namePh} className={inputCls} />
                </div>
                <div>
                  <label htmlFor="contact-email" className="block text-sm font-semibold text-slate-700 mb-2">{t.contact.email}</label>
                  <input id="contact-email" data-testid="contact-email-input" required type="email" value={form.email} onChange={set("email")} placeholder={t.contact.emailPh} className={inputCls} dir="ltr" />
                </div>
              </div>
              <div>
                <label htmlFor="contact-subject" className="block text-sm font-semibold text-slate-700 mb-2">{t.contact.subject}</label>
                <input id="contact-subject" data-testid="contact-subject-input" required minLength={2} value={form.subject} onChange={set("subject")} placeholder={t.contact.subjectPh} className={inputCls} />
              </div>
              <div>
                <label htmlFor="contact-message" className="block text-sm font-semibold text-slate-700 mb-2">{t.contact.message}</label>
                <textarea id="contact-message" data-testid="contact-message-input" required minLength={5} rows={5} value={form.message} onChange={set("message")} placeholder={t.contact.messagePh} className={`${inputCls} resize-none`} />
              </div>
              <button
                data-testid="contact-submit-button"
                type="submit"
                disabled={sending}
                className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-8 py-3.5 rounded-full bg-slate-900 text-white font-bold text-sm hover:bg-lime-500 hover:text-slate-900 disabled:opacity-60 transition-colors"
              >
                <Send size={16} />
                {sending ? t.contact.sending : t.contact.submit}
              </button>
            </form>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
